/**
 * ReminderSystem — in-app reminder popup for Account Executives.
 *
 * Mounted once at the AppLayout level. Only active for role='account_executive'.
 * - Polls /api/reminders/due every 30s with a `since` cursor
 * - Plays a persistent beep + shows a fixed bottom-right stack of reminder cards
 * - Also fires browser Notifications (after one-time permission prompt)
 * - Sound stops only when the user clicks Mark Complete / Dismiss / View Lead
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellRing, CheckCircle, X, ExternalLink, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { logger } from '../utils/logger';
import LeadStageChangeModal from './LeadStageChangeModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 30_000;

function formatDueTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// Web Audio API beep generator (avoids shipping an audio file)
function useBeepLoop() {
  const ctxRef = useRef(null);
  const intervalRef = useRef(null);

  const start = useCallback(() => {
    if (intervalRef.current) return;
    try {
      if (!ctxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        ctxRef.current = new AC();
      }
      const ctx = ctxRef.current;
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});

      const beep = () => {
        if (!ctxRef.current) return;
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, now);
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.25, now + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.3);

        // Second tone
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(1175, now + 0.32);
        gain2.gain.setValueAtTime(0.0001, now + 0.32);
        gain2.gain.exponentialRampToValueAtTime(0.25, now + 0.34);
        gain2.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.start(now + 0.32);
        osc2.stop(now + 0.62);
      };

      beep();
      intervalRef.current = setInterval(beep, 1500);
    } catch (err) {
      logger.error('Beep start failed', err);
    }
  }, []);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => () => stop(), [stop]);
  return { start, stop };
}

export default function ReminderSystem({ user }) {
  const navigate = useNavigate();
  const isAE = user?.role === 'account_executive';
  const [active, setActive] = useState([]); // list of reminder items currently shown
  const dismissedRef = useRef(new Set()); // reminder_id set — cleared on unmount
  const sinceRef = useRef(new Date().toISOString()); // only items due after login
  const pollingRef = useRef(null);
  const { start: startBeep, stop: stopBeep } = useBeepLoop();

  // Settings state
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [leadTimeMin, setLeadTimeMin] = useState(0);
  const [leadTimeDraft, setLeadTimeDraft] = useState('0');
  const [savingSettings, setSavingSettings] = useState(false);

  // Post-complete stage-change modal state
  const [stageConfig, setStageConfig] = useState(null);
  const [stageChangeModal, setStageChangeModal] = useState({
    open: false, leadId: null, currentStage: null,
  });

  // Request notification permission once on mount (AE only)
  useEffect(() => {
    if (!isAE) return;
    if (typeof Notification === 'undefined') return;
    if (Notification.permission === 'default') {
      // Delay the prompt slightly so it doesn't race the login flow
      const t = setTimeout(() => {
        Notification.requestPermission().catch(() => {});
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [isAE]);

  // Load initial settings and stage config (AE only)
  useEffect(() => {
    if (!isAE) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/reminders/settings`, { credentials: 'include' });
        if (r.ok) {
          const d = await r.json();
          setLeadTimeMin(d.lead_time_minutes || 0);
          setLeadTimeDraft(String(d.lead_time_minutes || 0));
        }
      } catch (err) {
        logger.error('Failed to load reminder settings', err);
      }
      try {
        const s = await fetch(`${API_URL}/api/stage-settings`, { credentials: 'include' });
        if (s.ok) setStageConfig(await s.json());
      } catch (err) {
        logger.error('Failed to load stage config for reminders', err);
      }
    })();
  }, [isAE]);

  const fireBrowserNotification = useCallback((item) => {
    if (typeof Notification === 'undefined') return;
    if (Notification.permission !== 'granted') return;
    try {
      const n = new Notification(
        item.type === 'task' ? 'Task reminder' : 'Appointment reminder',
        {
          body: `${item.title}${item.lead_name ? ' — ' + item.lead_name : ''}\nDue: ${formatDueTime(item.due_at)}`,
          tag: item.reminder_id,
          requireInteraction: true,
        }
      );
      n.onclick = () => {
        window.focus();
        navigate(item.type === 'appointment' ? '/appointments' : '/tasks');
        n.close();
      };
    } catch (err) {
      logger.error('Notification error', err);
    }
  }, [navigate]);

  const pollOnce = useCallback(async () => {
    if (!isAE) return;
    try {
      const url = `${API_URL}/api/reminders/due?since=${encodeURIComponent(sinceRef.current)}`;
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) return;
      const data = await r.json();
      const fresh = (data.items || []).filter(
        item => !dismissedRef.current.has(item.reminder_id)
      );
      if (!fresh.length) return;
      setActive(prev => {
        const existingIds = new Set(prev.map(p => p.reminder_id));
        const toAdd = fresh.filter(f => !existingIds.has(f.reminder_id));
        if (!toAdd.length) return prev;
        toAdd.forEach(fireBrowserNotification);
        return [...prev, ...toAdd];
      });
    } catch (err) {
      logger.error('Reminder poll error', err);
    }
  }, [isAE, fireBrowserNotification]);

  // Start polling
  useEffect(() => {
    if (!isAE) return;
    pollOnce(); // immediate
    pollingRef.current = setInterval(pollOnce, POLL_INTERVAL_MS);
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [isAE, pollOnce]);

  // Start/stop beep based on active queue size
  useEffect(() => {
    if (active.length > 0) startBeep();
    else stopBeep();
  }, [active.length, startBeep, stopBeep]);

  const removeItem = (reminderId) => {
    dismissedRef.current.add(reminderId);
    setActive(prev => prev.filter(i => i.reminder_id !== reminderId));
  };

  const handleDismiss = (item) => {
    removeItem(item.reminder_id);
  };

  const handleViewLead = (item) => {
    // Navigate to the relevant page where the user can open the lead detail sheet
    if (item.type === 'appointment') {
      navigate('/appointments');
    } else {
      navigate('/tasks');
    }
    removeItem(item.reminder_id);
  };

  const handleComplete = async (item) => {
    try {
      const r = await fetch(`${API_URL}/api/tasks/${item.target_id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ completion_notes: 'Completed via reminder popup' }),
      });
      if (r.ok) {
        toast.success('Task marked complete');
      } else {
        toast.error('Could not complete task');
        removeItem(item.reminder_id);
        return;
      }
    } catch (err) {
      logger.error('Complete error', err);
      toast.error('Could not complete task');
      removeItem(item.reminder_id);
      return;
    }
    removeItem(item.reminder_id);

    // Optional stage-change follow-up — only if the task is tied to a lead and
    // there are valid transitions from the current stage. The stage-change
    // modal has a "Skip" button so this is purely additive: declining it does
    // NOT un-complete the task.
    if (!item.lead_id || !stageConfig) return;
    try {
      const lr = await fetch(`${API_URL}/api/leads/${item.lead_id}`, { credentials: 'include' });
      if (!lr.ok) return;
      const lead = await lr.json();
      const currentStage = lead.stage;
      const allowed = (stageConfig.transitions || {})[currentStage] || [];
      if (allowed.length === 0) return;
      setStageChangeModal({
        open: true,
        leadId: item.lead_id,
        currentStage,
      });
    } catch (err) {
      logger.error('Post-complete stage fetch error', err);
    }
  };

  const dismissAll = () => {
    active.forEach(i => dismissedRef.current.add(i.reminder_id));
    setActive([]);
  };

  const saveSettings = async () => {
    const val = Math.max(0, Math.min(240, parseInt(leadTimeDraft, 10) || 0));
    setSavingSettings(true);
    try {
      const r = await fetch(`${API_URL}/api/reminders/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ lead_time_minutes: val }),
      });
      if (r.ok) {
        setLeadTimeMin(val);
        toast.success('Reminder settings saved');
        setSettingsOpen(false);
      } else {
        toast.error('Could not save settings');
      }
    } catch (err) {
      logger.error('Save settings error', err);
      toast.error('Could not save settings');
    }
    setSavingSettings(false);
  };

  if (!isAE) return null;

  return (
    <>
      {/* Floating bell icon (bottom-right, persistent, opens settings) */}
      <button
        type="button"
        onClick={() => setSettingsOpen(true)}
        className={`fixed bottom-4 right-4 z-40 h-11 w-11 rounded-full shadow-lg flex items-center justify-center transition-colors ${
          active.length > 0
            ? 'bg-red-500 text-white animate-pulse'
            : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
        }`}
        title="Reminder settings"
        data-testid="reminder-bell"
      >
        {active.length > 0 ? <BellRing size={20} /> : <Bell size={18} />}
        {active.length > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] flex items-center justify-center bg-red-700 text-white text-[10px] font-bold rounded-full px-1 border-2 border-white">
            {active.length}
          </span>
        )}
      </button>

      {/* Reminder popup stack (bottom-right, above the bell) */}
      {active.length > 0 && (
        <div
          className="fixed bottom-20 right-4 z-50 flex flex-col-reverse gap-3 max-h-[80vh] overflow-y-auto w-[min(92vw,380px)]"
          data-testid="reminder-stack"
        >
          {active.length > 1 && (
            <div className="flex justify-end">
              <Button
                variant="outline" size="sm" onClick={dismissAll}
                data-testid="reminder-dismiss-all"
                className="h-7 text-xs bg-white shadow"
              >
                Dismiss all ({active.length})
              </Button>
            </div>
          )}
          {active.map(item => (
            <div
              key={item.reminder_id}
              className="bg-white border-2 border-red-400 rounded-xl shadow-2xl p-4 animate-in slide-in-from-right"
              data-testid={`reminder-card-${item.type}-${item.target_id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded-lg ${item.type === 'task' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}`}>
                    {item.type === 'task' ? <BellRing size={16} /> : <Bell size={16} />}
                  </div>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    {item.type === 'task' ? 'Task reminder' : 'Appointment reminder'}
                  </span>
                </div>
                <button
                  onClick={() => handleDismiss(item)}
                  className="text-slate-400 hover:text-slate-700"
                  aria-label="Dismiss"
                  data-testid={`reminder-dismiss-${item.reminder_id}`}
                >
                  <X size={16} />
                </button>
              </div>
              <p className="text-sm font-bold text-slate-900 mb-1">{item.title}</p>
              {item.lead_name && (
                <p className="text-xs text-slate-600 mb-1">Lead: {item.lead_name}</p>
              )}
              <p className="text-xs text-slate-500 mb-3">Due: {formatDueTime(item.due_at)}</p>
              {item.description && (
                <p className="text-xs text-slate-500 mb-3 line-clamp-2">{item.description}</p>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {item.type === 'task' && (
                  <Button
                    size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleComplete(item)}
                    data-testid={`reminder-complete-${item.reminder_id}`}
                  >
                    <CheckCircle size={12} className="mr-1" /> Mark Complete
                  </Button>
                )}
                {item.lead_id && (
                  <Button
                    size="sm" variant="outline" className="h-7 text-xs"
                    onClick={() => handleViewLead(item)}
                    data-testid={`reminder-view-lead-${item.reminder_id}`}
                  >
                    <ExternalLink size={12} className="mr-1" /> {item.type === 'appointment' ? 'View Appt' : 'View Task'}
                  </Button>
                )}
                <Button
                  size="sm" variant="ghost" className="h-7 text-xs text-slate-500"
                  onClick={() => handleDismiss(item)}
                  data-testid={`reminder-dismiss-btn-${item.reminder_id}`}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings size={18} /> Reminder Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="reminder-lead-time">Remind me before due time (minutes)</Label>
              <Input
                id="reminder-lead-time"
                type="number"
                min={0}
                max={240}
                value={leadTimeDraft}
                onChange={(e) => setLeadTimeDraft(e.target.value)}
                data-testid="reminder-lead-time-input"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                0 = fire exactly at due time. Max 240 minutes (4 hours).
              </p>
            </div>
            <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
              <p className="font-semibold mb-1">How reminders work</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>In-app popup appears in the bottom-right with a beeping sound.</li>
                <li>Sound keeps beeping until you click a button on the reminder.</li>
                <li>Browser notifications also fire if you grant permission.</li>
                <li>Current lead time: <b>{leadTimeMin} min</b></li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancel</Button>
            <Button onClick={saveSettings} disabled={savingSettings} data-testid="reminder-save-settings">
              {savingSettings ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Post-complete stage-change modal (optional; Skip keeps task completed) */}
      <LeadStageChangeModal
        open={stageChangeModal.open}
        onOpenChange={(v) => setStageChangeModal(prev => ({ ...prev, open: v }))}
        leadId={stageChangeModal.leadId}
        currentStage={stageChangeModal.currentStage}
        stageConfig={stageConfig}
        title="Update lead stage? (optional)"
        skipLabel="Skip"
        onSuccess={() => setStageChangeModal({ open: false, leadId: null, currentStage: null })}
      />
    </>
  );
}
